<template>
    <div
        class="ls-comment"
        :class="{ 'ls-comment--is-editing': isModifying, 'ls-comment--no-bottom-border': isLast }"
    >
        <div
            class="ls-comment--top"
            :class="{ 'ls-comment--top-is-internal': comment.is_internal }"
        >
            <span style="white-space: pre-line">{{ comment.text }}</span>
        </div>
        <div v-if="!isModifying" class="ls-comment--bottom">
            <div>
                <span class="ls-comment--author"
                    >{{ author }}<br />{{ comment.updated_at | timestamp }}</span
                >
            </div>
            <div v-if="!comment.is_partners_portal" class="ls-comment--buttons">
                <LsButton
                    v-tooltip="comment.is_internal ? 'Show to provider' : 'Hide from provider'"
                    class="ls-comment--button"
                    track-id="ls_comment-update_is_internal"
                    :icon="!comment.is_internal ? 'view' : 'hide'"
                    :loading="isUpdating"
                    :outlined="!comment.is_internal"
                    :text-color="!comment.is_internal ? 'primary' : 'white'"
                    @click="updateOrDeleteComment"
                />

                <LsButton
                    v-tooltip="'Edit'"
                    class="ls-comment--button"
                    track-id="ls_comment-edit"
                    icon="edit"
                    :disabled="isUpdating"
                    outlined
                    text-color="primary"
                    @click="isEditing = true"
                />

                <LsButton
                    v-tooltip="'Delete'"
                    class="ls-comment--button"
                    track-id="ls_comment-delete"
                    icon="delete"
                    :disabled="isUpdating"
                    outlined
                    text-color="primary"
                    @click="isDeleting = true"
                />
            </div>
        </div>
        <div v-else>
            <div class="ls-comment--edit">
                <LsInput
                    v-if="isEditing"
                    v-model="text"
                    placeholder="Edit note here"
                    type="textarea"
                    track-id="ls_comment-edit_text"
                    name="text"
                    maxlength="1000"
                />
                <span v-else>Are you sure you want to delete this note?</span>
            </div>
            <div class="ls-comment--buttons">
                <LsButton
                    :track-id="`ls_comment-${isEditing ? 'edit' : 'delete'}_submit`"
                    :text="`${isEditing ? 'Update' : 'Delete'} note`"
                    :loading="isUpdating"
                    :disabled="!text"
                    @click="updateOrDeleteComment"
                />
                <LsButton
                    :track-id="`ls_comment-${isEditing ? 'edit' : 'delete'}_cancel`"
                    text="Cancel"
                    :disabled="isUpdating"
                    outlined
                    text-color="primary"
                    @click="modifyCommentCancel"
                />
            </div>
        </div>
    </div>
</template>

<script>
import VueTypes from 'vue-types';
import _ from 'lodash';
import { lsErrorService } from 'ls-vue-common';
import gql from 'graphql-tag';
import moment from 'moment-timezone';
import { apolloClient } from '@/vue-apollo';
import { LsButton, LsInput } from '@/components/common';
import UpdateComment from '@/graphql/mutations/UpdateComment.gql';
import DeleteComment from '@/graphql/mutations/DeleteComment.gql';

export default {
    name: 'LsComment',
    components: { LsButton, LsInput },
    props: {
        comment: VueTypes.shape({
            id: VueTypes.string.isRequired,
            text: VueTypes.string.isRequired,
            is_internal: VueTypes.bool.isRequired,
            is_partners_portal: VueTypes.bool.def(false),
            author: VueTypes.shape({
                first_name: VueTypes.string.def(''),
                last_name: VueTypes.string.def(''),
                is_admin: VueTypes.bool.def(false),
            }).isRequired.loose,
            updated_at: VueTypes.string.isRequired,
        }).isRequired.loose,
        isLast: VueTypes.bool.isRequired,
    },
    data() {
        return {
            text: this.comment.text,
            isUpdating: false,
            isEditing: false,
            isDeleting: false,
        };
    },
    computed: {
        author() {
            const { first_name, last_name, is_admin } = this.comment.author;
            return this.comment.is_partners_portal
                ? `Partners Portal`
                : `${first_name} ${last_name} - ${is_admin ? 'LawnStarter Support' : 'Provider'}`;
        },
        isModifying() {
            return this.isDeleting || this.isEditing;
        },
    },
    methods: {
        async updateOrDeleteComment() {
            this.isUpdating = true;
            if (this.isDeleting) {
                this.$apollo
                    .mutate({
                        mutation: DeleteComment,
                        variables: {
                            id: _.toNumber(this.comment.id),
                        },
                    })
                    .then(() => {
                        this.isDeleting = false;
                        // future improvement - api/graph could return soft deleted record to handle this
                        apolloClient.writeFragment({
                            id: `Comment:${this.comment.id}`,
                            fragment: gql`
                                fragment comment on Comment {
                                    deleted_at
                                }
                            `,
                            data: {
                                __typename: 'Comment',
                                deleted_at: moment().format('YYYY-MM-DD HH:mm:ss'),
                            },
                        });
                    })
                    .catch((error) => {
                        lsErrorService.log({ error, showAlert: true });
                    })
                    .finally(() => {
                        this.isUpdating = false;
                    });
                return;
            }
            this.$apollo
                .mutate({
                    mutation: UpdateComment,
                    variables: {
                        id: _.toNumber(this.comment.id),
                        is_internal: this.isEditing ? undefined : !this.comment.is_internal,
                        text: this.isEditing ? this.text : undefined,
                    },
                })
                .then(() => {
                    this.isEditing = false;
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating = false;
                });
        },
        modifyCommentCancel() {
            this.text = this.comment.text;
            this.isEditing = false;
            this.isDeleting = false;
        },
    },
};
</script>

<style lang="scss">
.ls-comment--is-editing {
    border-top: 1px dashed $primary;
    border-bottom: 1px dashed $primary;
    padding: 15px 0;
}

.ls-comment--no-bottom-border {
    border-bottom: none;
    padding-bottom: 0;
}

.ls-comment {
    .ls-comment--top {
        padding: 10px;
        margin-bottom: 5px;
        background: $secondary;
        color: $white;
        overflow-wrap: break-word;
    }

    .ls-comment--top-is-internal {
        background: $secondary-light;
        color: $secondary-dark;
    }

    .ls-comment--bottom {
        display: flex;
        justify-content: space-between;
    }

    .ls-comment--author {
        font-size: $size-6;
    }

    .ls-comment--buttons {
        display: flex;
        :not(:last-child) {
            margin-right: 5px;
        }
    }

    .ls-comment--button {
        height: 30px;
        width: 30px;
    }

    .ls-comment--edit {
        margin: 15px 0;
        span {
            font-weight: $bold;
        }
    }

    .textarea:not([rows]) {
        min-height: 58px;
    }
}
</style>
